/**
 * A horizontal strip of media that opens full size when clicked.
 *
 * Scroll-snap rather than a JavaScript slider: the browser already does momentum,
 * touch, trackpad and keyboard scrolling correctly, and a hand-rolled transform
 * carousel has to reimplement all of it and usually gets the touch part wrong. The
 * arrows scroll the same container, so pointer, finger and keyboard all drive one
 * mechanism.
 *
 * Images, PDFs and videos all appear here; the lightbox is shared with
 * `AttachmentPreviews` so there is one full-size viewer in the product rather than
 * two that drift apart.
 */
import { useCallback, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, FileText, PlayCircle, ZoomIn } from "lucide-react";
import { MediaLightbox, isPdf, isVideo, type Attachment } from "@/components/AttachmentPreviews";
import ResponsiveImage from "@/components/ResponsiveImage";

export default function MediaCarousel({
  items,
  emptyLabel,
}: {
  items: Attachment[];
  /** Shown instead of an empty strip. Omit to render nothing at all. */
  emptyLabel?: string;
}) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const scrollByCards = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    // One card plus its gap, so a press advances by exactly one slide rather than an
    // arbitrary pixel amount that leaves a card half cut off.
    const card = track.querySelector("li");
    const step = card ? card.getBoundingClientRect().width + 14 : track.clientWidth * 0.8;
    track.scrollBy({ left: step * direction, behavior: "smooth" });
  }, []);

  if (items.length === 0) {
    return emptyLabel ? <p className="media-carousel__empty">{emptyLabel}</p> : null;
  }

  return (
    <div className="media-carousel">
      <ul className="media-carousel__track" ref={trackRef}>
        {items.map((item, index) => {
          const video = isVideo(item);
          const pdf = isPdf(item);

          return (
            <li key={`${item.url}-${index}`}>
              <button
                type="button"
                className="media-carousel__card"
                onClick={() => setOpenIndex(index)}
                aria-label={`Open ${item.label} full size`}
              >
                {video ? (
                  /*
                    The video element itself as the thumbnail, showing its first frame.
                    `preload="metadata"` is what makes that frame appear without
                    downloading the file, and muted+no-controls keeps it a still.
                  */
                  <video className="media-carousel__media" src={item.url} preload="metadata" muted playsInline tabIndex={-1} />
                ) : pdf ? (
                  <span className="media-carousel__doc" aria-hidden="true"><FileText size={30} /></span>
                ) : (
                  <ResponsiveImage className="media-carousel__media" src={item.url} alt="" loading="lazy" maxWidth={320} sizes="290px" />
                )}

                <span className="media-carousel__badge" aria-hidden="true">
                  {video ? <PlayCircle size={15} /> : pdf ? <FileText size={14} /> : <ZoomIn size={14} />}
                </span>

                <span className="media-carousel__caption">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {/*
        Arrows only when there is more than one card — two buttons that cannot scroll
        anything are noise. Hidden from assistive tech because the track itself is
        keyboard scrollable, so these duplicate an existing affordance for the pointer.
      */}
      {items.length > 1 && (
        <div className="media-carousel__nav" aria-hidden="true">
          <button type="button" onClick={() => scrollByCards(-1)} tabIndex={-1}><ChevronLeft size={18} /></button>
          <button type="button" onClick={() => scrollByCards(1)} tabIndex={-1}><ChevronRight size={18} /></button>
        </div>
      )}

      {openIndex !== null && items[openIndex] && (
        <MediaLightbox attachment={items[openIndex]} onClose={() => setOpenIndex(null)} />
      )}

      <style>{`
        .media-carousel { position: relative; }
        .media-carousel__empty {
          margin: 0;
          color: #64748B;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
        }

        .media-carousel__track {
          display: flex;
          gap: 1rem;
          margin: 0;
          padding: .35rem .25rem 1rem;
          list-style: none;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,.18) transparent;
        }
        .media-carousel__track > li { scroll-snap-align: start; flex: 0 0 auto; }
        .media-carousel__track::-webkit-scrollbar { height: 6px; }
        .media-carousel__track::-webkit-scrollbar-thumb { background: rgba(255,255,255,.16); border-radius: 999px; }

        /*
          A glass card that asks to be pressed.

          The previous version was a flush thumbnail with a hairline border and the
          caption butted against the image — it read as a picture, not a control, so
          nothing suggested clicking it would open anything. This gives it real
          padding, an inset frame around the media, a frosted surface and a shadow
          that deepens on hover, which is the vocabulary the rest of the page uses for
          things you press.
        */
        .media-carousel__card {
          position: relative;
          display: block;
          /* 25% larger than the 232px this started at. */
          width: 290px;
          padding: .85rem;
          border: 1px solid rgba(255,255,255,.14);
          border-radius: 1.1rem;
          background: linear-gradient(155deg, rgba(255,255,255,.09), rgba(255,255,255,.028) 52%, rgba(255,255,255,.06));
          /* Frosts the page behind the card, which is what makes it read as glass. */
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.16),
            0 14px 32px rgba(0,0,0,.34);
          color: #E2E8F0;
          text-align: left;
          cursor: pointer;
          transition: border-color 200ms ease, transform 200ms ease, box-shadow 200ms ease, background 200ms ease;
        }
        .media-carousel__card:hover,
        .media-carousel__card:focus-visible {
          border-color: rgba(233,30,140,.55);
          background: linear-gradient(155deg, rgba(233,30,140,.16), rgba(255,255,255,.04) 55%, rgba(255,255,255,.08));
          transform: translateY(-4px);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.22),
            0 22px 46px rgba(0,0,0,.44),
            0 0 34px rgba(233,30,140,.18);
        }

        /* Inset, with its own frame, so the media sits *inside* the card. */
        .media-carousel__media,
        .media-carousel__doc {
          display: block;
          width: 100%;
          aspect-ratio: 16 / 10;
          object-fit: cover;
          border-radius: .7rem;
          border: 1px solid rgba(255,255,255,.08);
          background: #05070D;
        }
        .media-carousel__doc { display: grid; place-items: center; color: #7E8CA0; }

        .media-carousel__badge {
          position: absolute;
          top: 1.35rem;
          right: 1.35rem;
          display: grid;
          place-items: center;
          width: 30px;
          height: 30px;
          border-radius: .55rem;
          border: 1px solid rgba(255,255,255,.16);
          background: rgba(5,7,13,.72);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          color: #F9A8D4;
        }

        .media-carousel__caption {
          display: block;
          padding: .8rem .15rem .1rem;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 600;
          line-height: 1.35;
          /* Two lines then ellipsis, so one long title cannot make a card taller
             than its neighbours and break the strip's alignment. */
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .media-carousel__nav {
          display: flex;
          justify-content: flex-end;
          gap: .4rem;
          margin-top: .35rem;
        }
        .media-carousel__nav button {
          display: grid;
          place-items: center;
          width: 34px;
          height: 34px;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: .5rem;
          background: rgba(255,255,255,.04);
          color: #CBD5E1;
          cursor: pointer;
        }
        .media-carousel__nav button:hover { border-color: rgba(233,30,140,.5); color: #fff; }

        @media (max-width: 520px) {
          .media-carousel__card { width: 232px; padding: .7rem; }
        }
      `}</style>
    </div>
  );
}
